import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { db } from '../config/firebase';
import { doc, setDoc, serverTimestamp, updateDoc, increment } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

export default function ScheduleMeetingModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  // Form state
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [duration, setDuration] = useState(30); // minutes
  const [description, setDescription] = useState('');
  const [useAccessCode, setUseAccessCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [enableWaitingRoom, setEnableWaitingRoom] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time into ISO string
      const scheduledDateTime = new Date(`${meetingDate}T${meetingTime}`);

      if (scheduledDateTime < new Date()) {
        toast.error('Scheduled time must be in the future');
        setLoading(false);
        return;
      }

      // Generate 8-digit numeric meeting code
      const meetingId = Math.floor(10000000 + Math.random() * 90000000).toString();

      // Create scheduled meeting document with iOS-compatible schema
      await setDoc(doc(db, 'meetings', meetingId), {
        id: meetingId,
        meetingCode: meetingId,  // iOS compatibility
        name: meetingTitle.trim() || `Meeting ${meetingId}`,
        description: description.trim(),
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        scheduledFor: Math.floor(scheduledDateTime.getTime() / 1000),  // Seconds since epoch for iOS
        duration: duration, // in minutes
        status: 'scheduled',  // iOS status lifecycle: scheduled → waiting → active → ended
        hasAccessCode: useAccessCode,
        accessCode: useAccessCode ? accessCode : null,
        waitingRoomEnabled: enableWaitingRoom,
        saveChatHistory: true,
        participants: [],
        guestCount: 0  // iOS compatibility
      });

      // Update user's scheduled meetings count
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'usage.scheduledMeetingsCount': increment(1)
      });

      toast.success('Meeting scheduled successfully!');
      onSuccess?.(meetingId);
      onClose();
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error('Failed to schedule meeting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get min date (today) in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">Schedule Meeting</h3>
            <p className="text-[#6e6e73] dark:text-[#8e8e93] mt-1">Plan a meeting for a future date and time</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] rounded-lg transition"
          >
            <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Meeting Title */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Meeting Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              placeholder="e.g., Team Standup, Client Review"
              maxLength="100"
              required
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                min={today}
                required
                className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={meetingTime}
                onChange={(e) => setMeetingTime(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Duration (minutes)
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Description <span className="text-[#6e6e73] dark:text-[#8e8e93] font-normal">(Optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add meeting agenda or notes..."
              rows={3}
              maxLength="500"
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all resize-none"
            />
            <p className="text-xs text-[#6e6e73] dark:text-[#8e8e93] mt-1">
              {description.length}/500 characters
            </p>
          </div>

          {/* Access Code Toggle */}
          <div>
            <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
              <div>
                <p className="font-medium text-[#1d1d1f] dark:text-white">Require Access Code</p>
                <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">Only people with the code can join</p>
              </div>
              <input
                type="checkbox"
                checked={useAccessCode}
                onChange={(e) => setUseAccessCode(e.target.checked)}
                className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                style={{
                  backgroundImage: useAccessCode
                    ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                    : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                }}
              />
            </label>
          </div>

          {/* Access Code Input */}
          {useAccessCode && (
            <div className="animate-slide-up">
              <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                Access Code
              </label>
              <input
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                pattern="[0-9]{6}"
                className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
              />
              <p className="text-xs text-[#6e6e73] dark:text-[#8e8e93] mt-2">Share this code with meeting participants</p>
            </div>
          )}

          {/* Waiting Room Toggle */}
          <div>
            <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
              <div>
                <p className="font-medium text-[#1d1d1f] dark:text-white">Enable Waiting Room</p>
                <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">Review and admit participants before they join</p>
              </div>
              <input
                type="checkbox"
                checked={enableWaitingRoom}
                onChange={(e) => setEnableWaitingRoom(e.target.checked)}
                className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                style={{
                  backgroundImage: enableWaitingRoom
                    ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                    : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                }}
              />
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (useAccessCode && accessCode.length !== 6)}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-medium hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Scheduling...
                </>
              ) : (
                'Schedule Meeting'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
